import {
    MdasQuoteStreamingRequest,
    MdasQuoteStreamingResponse,
    QuoteStreamingData,
    QuoteStreamingDataSource,
    WatchmenQuoteStreamingRequest,
    WatchmenQuoteStreamingResponse,
} from './types/WebSocket.type';
import { parseOptionSymbol } from './utils';
import { WebSocketHandler, WebSocketHandlerOptions } from './WebSocketHandler';

export type QuoteStreamingHandlerOptions = Omit<
    WebSocketHandlerOptions,
    'protocols' | 'getHeartbeatPayload' | 'onMessage'
> & {
    dataSource: QuoteStreamingDataSource;
    getUrl: () => string;
    onMessage?: (quotes: QuoteStreamingData[]) => void;
};

/**
 * Facade over a WebSocket connection that normalizes MDAS and Watchmen quote
 * messages into a single unified QuoteStreamingData format.
 *
 * Callers interact exclusively through subscribe / unsubscribe and receive
 * normalized data via onMessage — no knowledge of the underlying protocol
 * or data source is required.
 */
export class QuoteStreamingHandler {
    private wsHandler: WebSocketHandler<MdasQuoteStreamingResponse | WatchmenQuoteStreamingResponse>;
    private source: QuoteStreamingDataSource;

    constructor(options: QuoteStreamingHandlerOptions) {
        const { dataSource, getUrl, onMessage, onReconnect, autoReconnect = true, ...rest } = options;
        this.source = dataSource;

        this.wsHandler = new WebSocketHandler(getUrl(), {
            ...rest,
            autoReconnect,
            heartbeatInterval: 30000,
            getHeartbeatPayload: () =>
                this.source === QuoteStreamingDataSource.Mdas ? { type: 'ping' } : { action: 'ping' },
            onReconnect: (event) => {
                this.wsHandler.updateUrl(getUrl());
                onReconnect?.(event);
            },
            onMessage: (event) => {
                const data = JSON.parse(event.data as unknown as string);
                const quotes =
                    this.source === QuoteStreamingDataSource.Mdas
                        ? this.normalizeMdas(data as MdasQuoteStreamingResponse)
                        : this.normalizeWatchmen(data as WatchmenQuoteStreamingResponse);

                if (quotes.length > 0) {
                    onMessage?.(quotes);
                }
            },
        });
    }

    get status() {
        return this.wsHandler.status;
    }

    get isOpen() {
        return this.wsHandler.isOpen;
    }

    close(code?: number, reason?: string) {
        this.wsHandler.close(code, reason);
    }

    subscribe(symbols: string | string[]): void {
        const arr = Array.isArray(symbols) ? symbols : [symbols];
        if (this.source === QuoteStreamingDataSource.Mdas) {
            for (const symbol of arr) {
                const option = !!parseOptionSymbol(symbol);
                this.wsHandler.json({
                    type: option ? 'queryoptionl1' : 'queryl1',
                    symbol,
                } satisfies MdasQuoteStreamingRequest);
            }
        } else {
            this.wsHandler.json({ action: 'sub', symbols: arr } satisfies WatchmenQuoteStreamingRequest);
        }
    }

    unsubscribe(symbols: string | string[]): void {
        const arr = Array.isArray(symbols) ? symbols : [symbols];
        if (this.source === QuoteStreamingDataSource.Mdas) {
            for (const symbol of arr) {
                const option = !!parseOptionSymbol(symbol);
                this.wsHandler.json({
                    type: option ? 'queryoptionl1' : 'queryl1',
                    symbol,
                    unsubscribe: true,
                } satisfies MdasQuoteStreamingRequest);
            }
        } else {
            this.wsHandler.json({ action: 'unsub', symbols: arr } satisfies WatchmenQuoteStreamingRequest);
        }
    }

    private normalizeMdas(response: MdasQuoteStreamingResponse): QuoteStreamingData[] {
        const responseRecord = response as unknown as Record<string, unknown>;
        const dataArray = (responseRecord['Data'] ?? responseRecord['data']) as
            MdasQuoteStreamingResponse['Data'] | undefined;
        const hasError = responseRecord['Error'] ?? responseRecord['error'];

        if (hasError || !dataArray || !Array.isArray(dataArray)) {
            return [];
        }

        return dataArray
            .filter((d) => !!d.Symbol)
            .map((d) => ({
                symbol: d.Symbol,
                lastPrice: d.LastPx,
                bid: d.BidPx,
                ask: d.AskPx,
                askSize: d.AskSz,
                bidSize: d.BidSz,
                volume: d.Volume,
                change: d.Change,
                changePercent: d.ChangePercent,
                dayHigh: d.HighPx,
                dayLow: d.LowPx,
                openingPrice: d.OpenPx,
                yesterdayClose: d.YestClosePx,
            }));
    }

    private normalizeWatchmen(response: WatchmenQuoteStreamingResponse): QuoteStreamingData[] {
        return Object.entries(response.u).map(([symbol, data]) => ({
            symbol,
            lastPrice: data.px,
            bid: data.bx,
            ask: data.ax,
            askSize: data.az,
            bidSize: data.bz,
            volume: data.v,
        }));
    }
}
